import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import StatusChip from '../../reusable/StatusChip/StatusChip';
import Skeleton from '../../reusable/Skeleton/Skeleton';
import { formatAccountSize, formatLoginId } from '../../utilities/accounts';
import { NA, phaseLabel, signedUsd, usd } from '../../utilities/format';
import { selectPickedAccount } from '../../store/slices/accountSlice';
import { useGetAccountsQuery } from '../../store/api/dashboardApi';
import {
  riskCounts,
  useGetAccountBreakdownQuery,
  useGetBreachDataQuery,
} from '../../store/api/metricsApi';
import { t } from '../../i18n/i18n';
import './BreachAnalysis.scss';

/** `max_per_asset_loss_breach` → "Max per asset loss breach". */
const humanize = (value) => {
  const text = String(value ?? '').replace(/_/g, ' ').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : NA;
};

/** `max_per_asset_loss_2_3_percent` → "Max per asset loss 2–3%". */
const ruleLabel = (value) =>
  humanize(
    String(value ?? '')
      .replace(/(\d+)_(\d+)_percent/g, '$1–$2%')
      .replace(/(\d+)_percent/g, '$1%'),
  );

const capitalise = (value) =>
  value ? String(value).charAt(0).toUpperCase() + String(value).slice(1) : NA;

const money = (value) => usd(value, { decimals: 2 });
const signedMoney = (value) => signedUsd(value, { decimals: 2 });

/** A price as the platform quotes it — every digit it sent, grouped. */
const price = (value) =>
  value == null || value === ''
    ? NA
    : Number(value).toLocaleString('en-US', { maximumFractionDigits: 5 });

/** "2%" from `rule_threshold_percent` (a fraction: 0.02). */
const percent = (fraction) =>
  fraction == null ? null : `${+(Number(fraction) * 100).toFixed(2)}%`;

/** An instant the engine records in UTC (`…+00:00`), in the viewer's own time. */
const when = (value) => {
  const at = value ? new Date(value) : null;
  return at && !Number.isNaN(at.getTime()) ? format(at, 'd MMM yyyy, HH:mm') : NA;
};

const toneOf = (n) => (Number(n) > 0 ? 'up' : Number(n) < 0 ? 'down' : 'neutral');

/** A card's title row — the 18px Figma glyph, the title, then its meta. */
function CardHead({ glyph, title, children }) {
  return (
    <>
      <header className="ba-card__head">
        <span className="ba-card__title">
          <img className="ba-card__glyph" src={`/icons/${glyph}.svg`} alt="" />
          {t(title)}
        </span>
        <span className="ba-card__meta">{children}</span>
      </header>
      <hr className="ba-card__rule" />
    </>
  );
}

function Field({ label, value }) {
  return (
    <div className="ba-field">
      <span className="ba-field__label">{t(label)}</span>
      <span className="ba-field__value">{value}</span>
    </div>
  );
}

function Block({ label, children }) {
  return (
    <div className="ba-block">
      <span className="ba-block__label">{t(label)}</span>
      {children}
    </div>
  );
}

/**
 * Breach Analysis — 1:32002. Laid out as the Figma frame draws it: the page
 * head, the Risk overview card, one card for the rule event (callout, the
 * event's fields, what happened, account information), then the Rule events
 * list. The frame's single "Related trade" grid is replaced by the two trade
 * tables the page added — the open positions at the breach and the closed
 * trades counted toward it — since a loss breach is rarely one trade.
 *
 * Reads the account's own breach from `/get/breach/data/<login>/`. The
 * endpoint answers one breach per account — the hard breach the auto-risk
 * engine recorded — or none ("Account is Active."). The market tick at the
 * breach and the reference id are left out on request.
 */
export default function BreachAnalysis() {
  const [params] = useSearchParams();
  const picked = useSelector(selectPickedAccount);
  const login = params.get('login') ?? picked.loginId ?? null;

  const { data, isLoading, isError } = useGetBreachDataQuery(login, { skip: !login });
  // The top bar's own query (same arguments, so the cache answers it) — the
  // account's stage and server for the Account information block.
  const { data: accountList } = useGetAccountsQuery({ page: 1, page_size: 100 });
  const account = (accountList?.accounts?.results ?? []).find(
    (row) => String(row.login_id) === String(login),
  );

  const breach = data?.data ?? null;
  const meta = breach?.meta_data ?? {};
  const rule = meta.breach_metadata ?? {};
  const calc = meta.calculation_breakdown ?? {};
  const accountLabel = login ? formatLoginId(login) : NA;
  const platform = meta.platform ?? account?.platform ?? picked.platform;
  const platformLabel = platform ? String(platform).toUpperCase() : NA;

  // The counts as Account Metrics shows them, from the account's own
  // `risk_status` (same arguments as its Simple view, so the cache can answer
  // it) — so both pages give the same numbers.
  const breakdown = useGetAccountBreakdownQuery(
    { loginId: login, platform, category: 'simple' },
    { skip: !login || !platform },
  );
  const { warnings: warningCount, hardBreaches } = riskCounts(breakdown.data?.risk_status);

  // The engine's own figures first, the record's top-level copies as fallback.
  const threshold = rule.rule_threshold_amount ?? calc.threshold ?? meta.threshold;
  const counted = rule.calculated_loss ?? calc.total_loss ?? meta.breach_loss;
  const exceededBy = rule.exceeded_by ?? calc.breach_amount;
  const pct = percent(rule.rule_threshold_percent ?? calc.threshold_pct);
  const accountSize = rule.account_size ?? meta.account_size ?? account?.account_size;
  const asset = rule.asset ?? String(meta.asset_key ?? '').split('_')[0];
  const direction = rule.direction ?? String(meta.asset_key ?? '').split('_')[1];
  const assetLabel = asset ? `${asset}${direction ? ` · ${capitalise(direction)}` : ''}` : NA;
  const detectedAt = when(rule.breach_detected_at ?? rule.breach_time ?? breach?.created_at);
  const title = humanize(meta.reason ?? breach?.category);
  const ruleName = rule.rule_name ? ruleLabel(rule.rule_name) : title;

  // The record is a hard breach unless it says it is only a warning.
  const isWarning = /warn/i.test(meta.severity ?? breach?.severity ?? breach?.category ?? '');
  const severity = breach ? (isWarning ? 'Warning' : 'Hard breach') : null;
  const tone = isWarning ? 'warning' : 'danger';

  const overview = [
    {
      label: 'Warnings',
      value: warningCount == null ? NA : String(warningCount),
      note: 'Recorded, account still active',
    },
    {
      label: 'Hard breaches',
      value: hardBreaches == null ? NA : String(hardBreaches),
      note: 'Rule violations with action',
    },
    {
      label: 'Payout status',
      value: hardBreaches == null ? NA : hardBreaches > 0 ? 'Blocked' : 'Not blocked',
      note:
        hardBreaches == null
          ? NA
          : hardBreaches > 0
            ? 'Hard breach prevents payout requests'
            : 'No hard breach on this account',
    },
  ];

  const openPositions = meta.open_positions?.length
    ? meta.open_positions
    : (meta.contributing_open_trades ?? []);
  const closedTrades = meta.contributing_closed_trades ?? [];
  const cooldownMinutes = meta.cooldown_chain?.cooldown_window_seconds
    ? Math.round(meta.cooldown_chain.cooldown_window_seconds / 60)
    : null;

  const whatHappened =
    breach &&
    t(
      pct
        ? '{{trigger}}: the loss counted on {{asset}} reached {{counted}} — {{floating}} floating in open positions and {{closed}} from closed trades — against a {{threshold}} limit ({{pct}} of the account). The auto-risk engine recorded the breach on {{detectedAt}}.'
        : '{{trigger}}: the loss counted on {{asset}} reached {{counted}} — {{floating}} floating in open positions and {{closed}} from closed trades — against a {{threshold}} limit. The auto-risk engine recorded the breach on {{detectedAt}}.',
      {
        trigger: t(humanize(rule.trigger_reason ?? 'loss breach')),
        asset: assetLabel,
        counted: money(counted),
        floating: money(calc.floating_loss ?? 0),
        closed: money(calc.closed_trade_loss ?? 0),
        threshold: money(threshold),
        pct,
        detectedAt,
      },
    );

  const accountRows = [
    ['Account', accountLabel],
    ['Platform', platformLabel],
    ['Server', meta.server ?? account?.server ?? account?.challenge?.server ?? NA],
    ['Account size', formatAccountSize(accountSize)],
    ['Stage', t(account?.status ?? phaseLabel(account?.stage) ?? NA)],
    ['Product', account?.product ?? 'Alpha Capital'],
  ];

  return (
    <div className="breach-analysis">
      <header className="ba-head">
        <h1>{t('Breach Analysis')}</h1>
        <p>
          {t(
            'Rule warnings and hard breaches flagged on your account by the auto-risk system. See which rule was triggered, what happened, and whether payout eligibility is affected.',
          )}
        </p>
      </header>

      {/* ---- Risk overview ---- */}
      <section className="ba-card">
        <CardHead glyph="ba-risk" title="Risk overview">
          {login && `${accountLabel} · Alpha Capital`}
        </CardHead>
        <div className="ba-grid ba-grid--stats">
          {overview.map((stat) => (
            <div key={stat.label} className="ba-stat">
              <span className="ba-stat__label">{t(stat.label)}</span>
              <span className="ba-stat__value">
                {isLoading ? <Skeleton w={56} h={18} /> : t(stat.value)}
              </span>
              <span className="ba-stat__note">{t(stat.note)}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ---- the rule event ---- */}
      {isLoading ? (
        <section className="ba-card">
          <div className="ba-detail">
            <Skeleton w="40%" h={16} />
            <Skeleton w="100%" h={84} radius={10} />
            <Skeleton w="100%" h={40} />
            <Skeleton w="70%" h={32} />
          </div>
        </section>
      ) : !breach ? (
        <section className="ba-card">
          <CardHead
            glyph="ba-event"
            title={!login ? 'No account selected' : isError ? 'Could not load breaches' : 'No breaches'}
          />
          <p className="ba-text">
            {!login
              ? t('Open an account from Account Metrics to see its breaches.')
              : isError
                ? t('The breach record for this account could not be loaded. Try again shortly.')
                : t('The auto-risk system has not recorded a breach on this account.')}
          </p>
        </section>
      ) : (
        <section className="ba-card">
          <CardHead glyph="ba-event" title={title}>
            <StatusChip tone={tone} size="md">
              {severity}
            </StatusChip>
            <span>{detectedAt}</span>
          </CardHead>

          <div className="ba-detail">
            <div className={`ba-callout ba-callout--${tone}`}>
              <span className="ba-callout__title">{t(severity)}</span>
              {isWarning ? (
                <>
                  <p>
                    {t(
                      'This is a rule warning. Your account may remain active, but further violations can escalate to a hard breach.',
                    )}
                  </p>
                  <p>{t('Your account remains active. A repeat violation can escalate to a hard breach.')}</p>
                </>
              ) : (
                <>
                  <p>
                    {t('The loss on {{asset}} passed the {{threshold}} per-asset limit by {{amount}}.', {
                      asset: assetLabel,
                      threshold: money(threshold),
                      amount: money(exceededBy),
                    })}
                  </p>
                  <p>{t('The rule violation has been actioned on your account. Payout requests are blocked.')}</p>
                </>
              )}
            </div>

            <div className="ba-grid">
              <Field label="Rule triggered" value={`${t(ruleName)}${pct ? ` (${pct})` : ''}`} />
              <Field label="Event time" value={detectedAt} />
              <Field label="Asset" value={assetLabel} />
              <Field label="Payout impact" value={isWarning ? t('No payout block') : t('Payout blocked')} />
            </div>

            <Block label="What happened">
              <p className="ba-text">{whatHappened}</p>
            </Block>

            <Block label="Account information">
              <div className="ba-grid">
                {accountRows.map(([label, value]) => (
                  <Field key={label} label={label} value={value} />
                ))}
              </div>
            </Block>

            <Block label={t('Open positions at time of breach ({{count}})', { count: openPositions.length })}>
              {openPositions.length > 0 ? (
                <div className="ba-table-wrap">
                  <table className="ba-table">
                    <thead>
                      <tr>
                        <th>{t('Position')}</th>
                        <th>{t('Side')}</th>
                        <th>{t('Symbol')}</th>
                        <th className="is-right">{t('Volume')}</th>
                        <th className="is-right">{t('Open price')}</th>
                        <th className="is-right">{t('SL')}</th>
                        <th className="is-right">{t('TP')}</th>
                        <th className="is-right">{t('Profit')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {openPositions.map((pos, index) => {
                        const ws = pos.ws_event || {};
                        const action = String(pos.action ?? pos.direction ?? '').toLowerCase();
                        const level = (key) =>
                          ws[key] != null && ws[key] !== ''
                            ? price(ws[key])
                            : pos[key] != null && pos[key] !== ''
                              ? price(pos[key])
                              : NA;
                        const profit =
                          ws.profit ?? pos.profit ?? pos.broker_profit ?? pos.pnl ?? pos.unrealized_pnl ?? 0;

                        return (
                          <tr key={pos.position_id ?? pos.ticket ?? index}>
                            <td className="is-muted">#{pos.position_id ?? pos.ticket ?? NA}</td>
                            <td>
                              <span
                                className={`ba-side${
                                  action.startsWith('b') ? ' is-buy' : action.startsWith('s') ? ' is-sell' : ''
                                }`}
                              >
                                {t(capitalise(pos.action ?? pos.direction))}
                              </span>
                            </td>
                            <td className="is-strong">{ws.symbol || pos.symbol || pos.raw_symbol || NA}</td>
                            <td className="is-right">{pos.volume ?? pos.lot_size ?? NA}</td>
                            <td className="is-right">{price(pos.open_price)}</td>
                            <td className="is-right">{level('sl')}</td>
                            <td className="is-right">{level('tp')}</td>
                            <td className={`is-right is-${toneOf(profit)}`}>{signedMoney(profit)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="ba-none">{t('No open positions at the time of the breach.')}</p>
              )}
            </Block>

            <Block label={t('Contributing closed trades ({{count}})', { count: closedTrades.length })}>
              {closedTrades.length > 0 ? (
                <div className="ba-table-wrap">
                  <table className="ba-table ba-table--closed">
                    <thead>
                      <tr>
                        <th>{t('Position')}</th>
                        <th>{t('Closed')}</th>
                        <th className="is-right">{t('Age')}</th>
                        <th className="is-right">{t('Loss')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {closedTrades.map((trade, index) => {
                        const pnl =
                          Number(
                            trade.pnl ??
                              trade.net_profit ??
                              (trade.realized_loss != null
                                ? -Math.abs(Number(trade.realized_loss))
                                : trade.loss != null
                                  ? -Math.abs(Number(trade.loss))
                                  : 0),
                          ) || 0;
                        const closedAt = trade.closed_ts
                          ? format(new Date(trade.closed_ts * 1000), 'd MMM yyyy, HH:mm:ss')
                          : trade.close_time_iso ?? trade.closed_at_iso
                            ? when(trade.close_time_iso ?? trade.closed_at_iso)
                            : trade.close_time ?? NA;

                        return (
                          <tr key={trade.position_id ?? trade.ticket ?? index}>
                            <td className="is-muted">#{trade.position_id ?? trade.ticket ?? NA}</td>
                            <td>{closedAt}</td>
                            <td className="is-right">{trade.age_sec != null ? `${trade.age_sec}s` : NA}</td>
                            <td className={`is-right is-${toneOf(pnl)}`}>{signedMoney(pnl)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="ba-none">{t('No closed trades counted toward this breach.')}</p>
              )}
              {cooldownMinutes != null && (
                <p className="ba-note">
                  {t(
                    'A position opened within {{minutes}} minutes of a losing trade on the same asset counts toward the same loss.',
                    { minutes: cooldownMinutes },
                  )}
                </p>
              )}
            </Block>
          </div>
        </section>
      )}

      {/* ---- Rule events ---- */}
      <section className="ba-events">
        <h2 className="ba-events__title">{t('Rule events ({{count}})', { count: breach ? 1 : 0 })}</h2>
        {breach && (
          <div className="ba-event is-active">
            <div className="ba-event__top">
              <span className="ba-event__rule">{t(ruleName)}</span>
              <StatusChip tone={tone} size="md">
                {severity}
              </StatusChip>
            </div>
            <span className="ba-event__title">{t(title)}</span>
            <span className="ba-event__at">{detectedAt}</span>
          </div>
        )}
      </section>
    </div>
  );
}
